import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router } from '@angular/router';
import { UserService } from '@app/services/user/user.service';
import { PermissionService } from '@core/services/permission/permission.service';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { Observable, of } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class PermissionGuard implements CanLoad, CanActivate, CanActivateChild {
	// @Todo: verify routes
	private readonly TOP_LEVEL_PAGES: TopPage[] = [
		{ key: 'dashboard', url: 'dashboard' },
		{ key: 'ec2', url: 'ec2' },
		{ key: 'zesty-disk', url: 'disk' },
		{ key: 'unused-resources', url: 'unused-resources' },
		{ key: 'my-organization', url: 'my-organization' },
		{ key: 'my-profile', url: 'my-profile' },
		{ key: 'admin-panel', url: 'cv' },
		{ key: 'partner-portal', url: 'partners' },
	];

	constructor(private permissionService: PermissionService, private userService: UserService, private router: Router) {}

	public canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean> {
		const permissionKey = childRoute.data.permissionKey;
		return this.isRoutePermitted(permissionKey);
	}

	public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
		const permissionKey = route.data.permissionKey;
		return this.isRoutePermitted(permissionKey);
	}

	public canLoad(route: Route): Observable<boolean> {
		const permissionKey = route.data.permissionKey;
		return this.isRoutePermitted(permissionKey);
	}

	private isRoutePermitted(permissionKey: string): Observable<boolean> {
		return this.userService.isAuthenticated$.pipe(
			take(1),
			concatMap((isAuthenticated: boolean) => {
				if (!isAuthenticated) {
					return this.userService.requestLogin().pipe(map(() => false));
				}
				if (!permissionKey) {
					return of(true);
				}
				return this.handlePermissions(permissionKey);
			}),
		);
	}

	private handlePermissions(permissionKey: string): Observable<boolean> {
		return this.permissionService.initPermission().pipe(
			concatMap((isOk) => {
				if (!isOk) {
					return this.userService.requestLogin().pipe(map(() => false));
				}
				return this.permissionService.isPermitted$(permissionKey).pipe(
					take(1),
					map((isPermitted: boolean) => {
						if (isPermitted) {
							return true;
						}
						const alternativePermittedRoute = this.findPermittedRoute();
						if (alternativePermittedRoute) {
							this.router.navigate([`/${alternativePermittedRoute}`]).then();
							return true;
						}
						this.router.navigate([`/${AppRouteHelper.error}`]).then();
						return false;
					}),
				);
			}),
		);
	}

	private findPermittedRoute(): string {
		let result = '';
		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let i = 0; i < this.TOP_LEVEL_PAGES.length; i++) {
			const permissionPageKey = this.TOP_LEVEL_PAGES[i].key;

			if (this.permissionService.isPermitted(permissionPageKey)) {
				result = this.TOP_LEVEL_PAGES[i].url;
				break;
			}
		}

		return result;
	}
}

interface TopPage {
	key: string;
	url: string;
}
